/**
 * HmWarehouse — kartoteka dokumentów magazynowych (WZ / PZ / MM- / MM+) z Symfonii.
 *
 * Kwoty na wielu dokumentach są ZEROWE i to prawdziwe dane, nie błąd mapowania: w Symfonii
 * wartość nosi dokument handlowy, a magazynowy bywa tylko ruchem ilościowym. Sprawdzone na
 * żywej instancji (5 z 75 dokumentów miało wartość) — nie „naprawiaj" tego wyliczaniem z pozycji.
 *
 * Kolumna magazynu docelowego ma sens wyłącznie dla przesunięć (MM), na pozostałych jest pusta.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmWarehouseListItem } from '@saas/shared';
import { Boxes } from 'lucide-react';

import type { ColumnDefinition } from '@/components/configurable-table';
import { HmBulkApprovalButton } from '@/components/handel-magazyn/HmBulkApprovalButton';
import { day, HmListShell } from '@/components/handel-magazyn/HmListShell';
import { ReportsMenuButton } from '@/components/handel-magazyn/ReportsMenuButton';
import { useApprovalColumn } from '@/components/handel-magazyn/useApprovalColumn';
import { WarehouseReportParamsModal } from '@/components/handel-magazyn/WarehouseReportParamsModal';
import {
  defaultWarehouseParams,
  toWarehouseSearchParams,
  WAREHOUSE_REPORT_SLUGS,
  warehouseReportDefaults,
  warehouseReportPath,
  type WarehouseReportSlug,
  warehouseReportTitleKey,
} from '@/lib/handel-magazyn/warehouse-reports-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

export function HmWarehouse() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useActiveCompanyScope();
  const [report, setReport] = useState<{
    slug: WarehouseReportSlug;
    dateFrom: string;
    dateTo: string;
    ids: number[];
  } | null>(null);

  const approvalColumn = useApprovalColumn<HmWarehouseListItem>('warehouse', scope.companyId);

  const columns = useMemo<ColumnDefinition<HmWarehouseListItem>[]>(
    () => [
      {
        id: 'documentNumber',
        header: t('tenant.handelMagazyn.columns.documentNumber'),
        accessor: (row) => row.documentNumber,
        defaultWidth: 170,
        renderCell: (_v: unknown, row: HmWarehouseListItem) => (
          <span className="flex items-center gap-2">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${row.inBuffer ? 'bg-text-tertiary' : 'bg-emerald-500'}`}
            />
            <span className="font-mono text-xs text-text-primary">{row.documentNumber}</span>
          </span>
        ),
      },
      {
        id: 'typeCode',
        header: t('tenant.handelMagazyn.columns.typeCode'),
        accessor: (row) => row.typeCode,
        defaultWidth: 80,
      },
      {
        id: 'issueDate',
        header: t('tenant.handelMagazyn.columns.issueDate'),
        accessor: (row) => day(row.issueDate),
        defaultWidth: 130,
      },
      {
        id: 'operationDate',
        header: t('tenant.handelMagazyn.warehouse.columns.operationDate'),
        accessor: (row) => day(row.operationDate),
        defaultWidth: 130,
      },
      {
        id: 'contractorName',
        header: t('tenant.handelMagazyn.columns.contractor'),
        accessor: (row) => row.contractorName ?? '—',
        defaultWidth: 240,
      },
      {
        id: 'warehouseName',
        header: t('tenant.handelMagazyn.warehouse.columns.warehouse'),
        accessor: (row) => row.warehouseName ?? '—',
        defaultWidth: 150,
      },
      {
        id: 'receivingWarehouseName',
        header: t('tenant.handelMagazyn.warehouse.columns.receivingWarehouse'),
        accessor: (row) => row.receivingWarehouseName ?? '—',
        defaultWidth: 150,
      },
      {
        id: 'netValue',
        header: t('tenant.handelMagazyn.columns.net'),
        accessor: (row) => row.netValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 120,
      },
      {
        id: 'documentValue',
        header: t('tenant.handelMagazyn.warehouse.columns.documentValue'),
        accessor: (row) => row.documentValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
        renderCell: (_v: unknown, row: HmWarehouseListItem) => (
          <span className="font-medium text-text-primary">{row.documentValue}</span>
        ),
      },
      // Stan obiegu akceptacji — jedyna kolumna czytana z NASZEJ bazy, nie z Symfonii.
      ...(approvalColumn ? [approvalColumn] : []),
    ],
    [t, approvalColumn],
  );

  /**
   * Zestawienia magazynowe — odpowiedniki pozycji z menu Symfonii. „Zestawienie wybranych
   * dokumentów" to ten sam raport zawężony do zaznaczonych; bez zaznaczenia jest wyłączone.
   */
  function reportItems(ctx: { dateFrom: string; dateTo: string; selected: HmWarehouseListItem[] }) {
    const ids = ctx.selected.map((row) => row.id);
    const open = (slug: WarehouseReportSlug, onlySelected: boolean) => () =>
      setReport({ slug, dateFrom: ctx.dateFrom, dateTo: ctx.dateTo, ids: onlySelected ? ids : [] });

    return [
      {
        key: 'selected',
        label: t('tenant.handelMagazyn.reports.forSelected', { count: ids.length }),
        onSelect: ids.length > 0 ? open('magazyn-za-okres', true) : undefined,
      },
      ...WAREHOUSE_REPORT_SLUGS.map((slug) => ({
        key: slug,
        label: t(warehouseReportTitleKey(slug)),
        onSelect: open(slug, false),
      })),
    ];
  }

  return (
    <>
      <HmListShell<HmWarehouseListItem>
        title={t('tenant.handelMagazyn.warehouse.title')}
        resource="warehouse-documents"
        queryKey="warehouse-documents"
        tableId="hm_warehouse"
        testIdPrefix="hm-warehouse"
        columns={columns}
        getRowId={(row) => String(row.id)}
        selectable
        renderSelectionAction={({ selected }) => (
          <HmBulkApprovalButton<HmWarehouseListItem>
            kind="warehouse"
            companyId={scope.companyId}
            selected={selected}
            getExternalId={(row) => row.id}
            getLabel={(row) => row.documentNumber}
          />
        )}
        renderReports={(ctx) => (
          <ReportsMenuButton items={reportItems(ctx)} testIdPrefix="hm-wh-reports" />
        )}
        emptyIcon={Boxes}
        groupBy={{ label: t('tenant.handelMagazyn.warehouse.kinds'), of: (row) => row.typeCode }}
        onRowClick={(row) => navigate(`/handel-magazyn/magazyn/${row.id}`)}
        onNew={() => navigate('/handel-magazyn/magazyn/nowy')}
      />
      {report && (
        <WarehouseReportParamsModal
          open
          title={t(warehouseReportTitleKey(report.slug))}
          initial={warehouseReportDefaults(
            report.slug,
            // Okno startuje od zakresu Z KARTOTEKI — przy zaznaczonych dokumentach inny zakres
            // po prostu by ich nie objął.
            { ...defaultWarehouseParams(report.dateFrom, report.dateTo), ids: report.ids },
          )}
          onClose={() => setReport(null)}
          onSubmit={(params) => {
            navigate(`${warehouseReportPath(report.slug)}?${toWarehouseSearchParams(params)}`);
            setReport(null);
          }}
        />
      )}
    </>
  );
}

export default HmWarehouse;
